import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item/Grid';
import { Message } from '@axo/ui-core/components/Message';
import { useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAxoFormContext } from '../../../../../context/AxoForm/AxoForm';
import { formatSpacedNumber } from '../../../../../utils/format';

const ManualDebtRegistry = () => {
  const { t } = useTranslation();

  const [isRefinanceAmountSet, setIsRefinanceAmountSet] = useState(false);

  const { watch, setValue, register } = useFormContext();

  const { onChange } = useAxoFormContext();

  const gridColumns = useMemo(() => ({ s: 1, m: 1, l: 2 }), []);

  const outstandingAmountValue = Number(
    watch('Debt/Personal/OutstandingAmount')
  );

  const outstandingAmountRegisterOptions: React.ComponentProps<
    typeof NumberFieldUI
  >['registerOptions'] = useMemo(
    () => ({
      required: 'error.required',
      onBlur: (e) => {
        if (!e.target.value) return;

        const amount = Number(e.target.value) ?? null;

        if (amount === 0) {
          const result = {};
          const purpose = Purpose.Other;

          setValue('LoanApplication/Purpose', purpose, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          Object.assign(result, {
            'LoanApplication/Purpose': purpose,
            'LoanApplication/TotalRefinancingAmount': amount,
          });

          onChange(result);
        }

        // Copy value of once to Debt/Personal/RefinanceAmount
        setIsRefinanceAmountSet(true);
        if (isRefinanceAmountSet) return;
        setValue('Debt/Personal/RefinanceAmount', e.target.value, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        });
      },
    }),
    [isRefinanceAmountSet, onChange, setValue]
  );

  const onRefinanceAmountChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const result = {};
      const amount = Number(e.target.value) ?? null;
      const purpose = amount > 0 ? Purpose.RefinancingLoan : Purpose.Other;

      setValue('LoanApplication/Purpose', purpose, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      setValue('LoanApplication/TotalRefinancingAmount', amount, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      Object.assign(result, {
        'LoanApplication/Purpose': purpose,
        'LoanApplication/TotalRefinancingAmount': amount,
      });

      onChange(result);
    },
    [onChange, setValue]
  );

  const refinanceAmountRegisterOptions: React.ComponentProps<
    typeof NumberFieldUI
  >['registerOptions'] = useMemo(
    () => ({
      required: 'error.required',
      shouldUnregister: true,
      onChange: onRefinanceAmountChange,
      onBlur: onRefinanceAmountChange,
    }),
    [onRefinanceAmountChange]
  );

  return (
    <>
      <Message hasIcon variant="error">
        {t('BankID.error.unavailable')}
      </Message>

      <Grid columns={gridColumns}>
        <input
          type="hidden"
          {...register('LoanApplication/TotalRefinancingAmount', {
            valueAsNumber: true,
          })}
        />

        <NumberFieldUI
          name="Debt/Personal/OutstandingAmount"
          label={t('Debt.Personal.OutstandingAmount.label')}
          suffix={t('suffix.currency')}
          data-testid="debt-personal-outstanding-amount-input"
          format={formatSpacedNumber}
          registerOptions={outstandingAmountRegisterOptions}
        />

        {outstandingAmountValue > 0 && (
          <NumberFieldUI
            name="Debt/Personal/RefinanceAmount"
            label={t('Debt.Personal.RefinanceAmount.label')}
            suffix={t('suffix.currency')}
            data-testid="debt-personal-refinance-amount-input"
            format={formatSpacedNumber}
            registerOptions={refinanceAmountRegisterOptions}
          />
        )}
      </Grid>
    </>
  );
};

export default ManualDebtRegistry;
